import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useUserStore } from '@/stores/UserStore'
import { useRoute } from 'vue-router'
import { POSTHOG_EVENTS, INVENTORY_BETA_SURFACES, INVENTORY_BETA_STATUS } from '@/constants'
import { isLaterSessionThan } from '@/helpers/session'
import { safeCapture, inventoryBetaArm, mirrorInventoryBetaStatus, posthogSessionId } from '@/helpers/posthog'

/**
 * One switch per page life, so both events fire EXACTLY once per switch
 * `setNewDesign` ends in a full reload, so a second call can
 * only come from a double-click on the toggle — which would fire the event
 * twice. Module scope, not composable scope: each component gets its own
 * `useNewDesign()` instance, and the guard has to hold across all of them.
 */
let switchInFlight = false

/**
 * Same reasoning as `switchInFlight`, for the offer-shown report: module scope,
 * because each component gets its own `useNewDesign()` instance and two entry
 * points rendering in the same tick must not both fire the event.
 */
let offerReportInFlight = false

/**
 * useNewDesign — reactive gate for the "new design" rollout.
 *
 * Single source of truth is `user.data.about.new_design` (surfaced by the
 * `UserStore.isNewDesign` getter). Everything that belongs to the new design
 * — new views, toolbars, action-slot buttons, layouts — must be gated on this
 * so the whole feature can be flipped from one place.
 *
 *   const { isNewDesign } = useNewDesign()
 *   <NewThing v-if="isNewDesign" />
 *   <OldThing v-else />
 *
 * Reactive: when the toggle flips `user.data.about.new_design`, every consumer
 * updates automatically.
 */
export function useNewDesign () {
  const userStore = useUserStore()
  const {
    isNewDesign,
    newDesignPromptDismissed,
    newDesignPromptDismissedFinal,
    newDesignTourSeen,
    newDesignOfferShown,
    inventoryBetaEligibleAt,
  } = storeToRefs(userStore)
  const route = useRoute()

  // Merge-patch `user.data.about`. The merge itself lives on the store, because
  // `markInventoryBetaEligible` — called from `CreateBatchStore`, which cannot
  // use this composable — needs the same write.
  const patchAbout = (partial) => userStore.patchAbout(partial)

  /**
   * Flip the design mode by persisting `user.data.about.new_design`. After it
   * resolves we do a full reload so AG Grid column defs, route guards and layout
   * chrome all re-evaluate cleanly under the new mode.
   *
   * `surface` names WHICH control the dealer used — the route (`from`) can't:
   * both welcome-modal stages, and every other opt-in surface on the Inventory
   * page, report the same `/inventory`. Values come from
   * `INVENTORY_BETA_SURFACES`; an uninstrumented caller reports `unknown`
   * rather than silently blending into whichever surface is busiest.
   *
   * What this function deliberately does NOT do any more is touch the
   * `inventory-beta` flag. The flag value is the dealer's ARM, set
   * once by PostHog at enrollment; this is their opt-in STATE. Two pieces of
   * state, two owners. Collapsing them moved dealers out of the arm they were
   * randomised into every time they switched back to legacy.
   */
  const setNewDesign = async (value, surface = INVENTORY_BETA_SURFACES.UNKNOWN) => {
    if (switchInFlight) return
    switchInFlight = true

    const optingIn = value === true
    const now = new Date().toISOString()

    // BEFORE the event, not after: person-on-events stamps
    // person properties at ingestion, so the event that marks the change — and
    // the opt-out survey that targets on this property — must both see the new
    // value, not the one it had a moment ago.
    mirrorInventoryBetaStatus(optingIn ? INVENTORY_BETA_STATUS.OPTED_IN : INVENTORY_BETA_STATUS.OPTED_OUT)

    /**
     * Only what the CLIENT knows. `stint_number`, `is_return`,
     * `days_since_opt_out` and `time_in_beta_days` used to ride along and are
     * gone: every one is a window function over this same event stream, and
     * computing them here needed durable client state the client doesn't have
     * — which is why `stint_number` arrived null on 45 of 52 opt-ins.
     *
     * `variant` is stamped explicitly rather than left to the flag: two
     * `opted_out` events already carry no arm at all because the flag hadn't
     * resolved when they fired, and an opt event that can't be attributed to an
     * arm is invisible to the comparison the experiment exists to make.
     *
     * `from` stays, but as context to read on a single event — never as a
     * breakdown dimension. It carries raw paths with batch ids in them, so
     * nearly every value is unique; `surface` is the dimension.
     */
    const eventProps = {
      variant: inventoryBetaArm(),
      surface,
      // "The dealer chose to leave", as opposed to a future system-driven
      // opt-out. The HUMAN reason comes from the opt-out survey.
      reason: 'user_initiated',
      from: route.fullPath,
    }

    safeCapture(
      optingIn ? POSTHOG_EVENTS.INVENTORY_BETA_OPTED_IN : POSTHOG_EVENTS.INVENTORY_BETA_OPTED_OUT,
      eventProps,
    )

    // Opting OUT is a decision, not a "not now". The welcome modal's stage-1
    // condition is `!new_design && !dismissed && !dismissed_final`, which is
    // EXACTLY the state you land in after switching to legacy — so a user who
    // opted in from the sidebar / user-menu (neither of which sets a dismissal)
    // got the "Welcome to Inventory Beta" invite thrown at them the instant
    // they left. Record the choice in the same patch; the explicit
    // "Try Inventory Beta" button is still there whenever they want back in.
    await patchAbout(optingIn
      ? {
          new_design: true,
          // The session they joined in. The survey waits for a LATER one
          // — asked inside this session, it would be asking what
          // they think of something they have not used yet. PostHog's own
          // session id, because that is the session it attributes the survey to.
          inventory_beta_opt_in_session_id: posthogSessionId(),
        }
      : {
          new_design: false,
          new_design_dismissed: true,
          new_design_dismissed_final: true,
          // Not for analytics any more — that's derived from the event stream
          // now. This is what tells the status mirror that a dealer sitting on
          // legacy CHOSE to be there, rather than never having engaged.
          new_design_opted_out_at: now,
        })

    window.location.href = route.fullPath
  }

  /**
   * Not in the session they became eligible in.
   *
   * A dealer becomes eligible the moment their first batch is created or an
   * import finishes — they are mid-task, looking at the result, and a modal
   * over that reads as an ad rather than an invitation. So the welcome prompt
   * waits for their NEXT visit.
   *
   * `true` when there's no stamp at all: everyone who was already eligible
   * before enrollment was gated on activity has nothing to wait for, and their
   * prompt behaviour is unchanged.
   */
  const promptSessionReady = computed(() => isLaterSessionThan(inventoryBetaEligibleAt.value))

  /**
   * Report that the beta offer was SEEN — once per dealer, ever.
   *
   * Without it, "saw the offer and didn't want it" and "never found the offer"
   * are the same shape in the data: a value problem and a discovery problem
   * look identical, and they call for opposite fixes.
   *
   * Once per DEALER, not once per render or per page load, so the flag lives on
   * the user record next to the rest of the beta state — `data.about`, the same
   * field as `new_design` — rather than in local storage, which would re-fire
   * the event on every new device. `new_design_offer_surface` records which
   * entry point got there first; later surfaces no-op.
   *
   * `offerReportInFlight` covers the gap the server round-trip leaves: several
   * entry points can be visible in the same tick (sidebar promo + welcome
   * modal), and all of them would read the same un-set flag.
   */
  const markBetaOfferShown = (surface = INVENTORY_BETA_SURFACES.UNKNOWN) => {
    if (offerReportInFlight || newDesignOfferShown.value) return

    offerReportInFlight = true

    safeCapture(POSTHOG_EVENTS.INVENTORY_BETA_OFFER_SHOWN, {
      variant: inventoryBetaArm(),
      surface,
    })

    return patchAbout({
      new_design_offer_shown_at: new Date().toISOString(),
      new_design_offer_surface: surface,
    })
  }

  /**
   * Persist the FIRST "Maybe later" dismissal. No reload — the user stays on the
   * old design; the stage-2 follow-up prompt appears after a delay.
   */
  const dismissNewDesignPrompt = () => patchAbout({ new_design_dismissed: true })

  /**
   * Persist the SECOND (final) "Maybe later" dismissal. No welcome modal is ever
   * shown again after this.
   */
  const dismissNewDesignPromptFinal = () => patchAbout({ new_design_dismissed_final: true })

  /** Persist that the first-run guided tour was completed/skipped. */
  const markNewDesignTourSeen = () => patchAbout({ new_design_tour_seen: true })

  /**
   * Flip the tour's seen flag — for TESTING the tour, which otherwise runs once
   * per account and can never be seen again. Exposed through a localhost-only
   * control in the header (`DevTourToggle`); the flag itself is the same one
   * production uses, so there is no test-only state to drift.
   */
  const toggleNewDesignTourSeen = () => patchAbout({ new_design_tour_seen: !newDesignTourSeen.value })

  return {
    /** `true` when the user has opted into the new design. */
    isNewDesign,
    /** Convenience inverse for `v-else`-free old-design branches. */
    isOldDesign: computed(() => !isNewDesign.value),
    /** `true` once the first welcome prompt has been dismissed. */
    newDesignPromptDismissed,
    /** `true` once the follow-up prompt has been dismissed (final). */
    newDesignPromptDismissedFinal,
    /** Stage 1: old design, nothing dismissed yet, and not this session. */
    shouldPromptNewDesign: computed(() =>
      !isNewDesign.value
      && promptSessionReady.value
      && !newDesignPromptDismissed.value
      && !newDesignPromptDismissedFinal.value),
    /** Stage 2: old design, first dismissed, follow-up not yet dismissed. */
    shouldPromptNewDesignSecond: computed(() =>
      !isNewDesign.value
      && promptSessionReady.value
      && newDesignPromptDismissed.value
      && !newDesignPromptDismissedFinal.value),
    /** `true` once the first-run guided tour has been seen. */
    newDesignTourSeen,
    /** Run the first-run tour: new design, on Inventory, tour not yet seen. */
    shouldRunNewDesignTour: computed(() =>
      isNewDesign.value && !newDesignTourSeen.value),
    /** `true` once the beta offer has been recorded as seen. */
    newDesignOfferShown,
    /** Report the offer as seen — once per dealer; later calls no-op. */
    markBetaOfferShown,
    /** Persist the design-mode flag (`true`/`false`) then reload into it. */
    setNewDesign,
    /** Persist the first "Maybe later" dismissal (no reload). */
    dismissNewDesignPrompt,
    /** Persist the final "Maybe later" dismissal (no reload). */
    dismissNewDesignPromptFinal,
    /** Persist that the first-run guided tour was completed/skipped. */
    markNewDesignTourSeen,
    /** Dev-only: flip the seen flag so the tour can be replayed. */
    toggleNewDesignTourSeen,
  }
}
